import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import { S, Icon, Icons, BtnPrimary, BtnSecondary, TH, TD, StatusBadge, formatDate, LOCALE_CONFIG } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Module Definitions (labels/descs resolved per-render via i18n) ──
const MODULES = [
  { key: 'claims', tName: 'screens.onboardOrg.modClaims', tDesc: 'screens.onboardOrg.modClaimsDesc', icon: 'file-text', core: true },
  { key: 'policies', tName: 'screens.onboardOrg.modPolicies', tDesc: 'screens.onboardOrg.modPoliciesDesc', icon: 'shield', core: true },
  { key: 'documentVault', tName: 'screens.onboardOrg.modVault', tDesc: 'screens.onboardOrg.modVaultDesc', icon: 'folder', core: true },
  { key: 'invoicing', tName: 'screens.onboardOrg.modInvoicing', tDesc: 'screens.onboardOrg.modInvoicingDesc', icon: 'receipt' },
  { key: 'commissions', tName: 'screens.onboardOrg.modCommissions', tDesc: 'screens.onboardOrg.modCommissionsDesc', icon: 'percent' },
  { key: 'brokers', tName: 'screens.onboardOrg.modBrokers', tDesc: 'screens.onboardOrg.modBrokersDesc', icon: 'briefcase' },
  { key: 'riskCarriers', tName: 'screens.onboardOrg.modRiskCarriers', tDesc: 'screens.onboardOrg.modRiskCarriersDesc', icon: 'building' },
  { key: 'compliance', tName: 'screens.onboardOrg.modCompliance', tDesc: 'screens.onboardOrg.modComplianceDesc', icon: 'check-circle' },
  { key: 'eSignatures', tName: 'screens.onboardOrg.modESignatures', tDesc: 'screens.onboardOrg.modESignaturesDesc', icon: 'pen-tool' },
  { key: 'aiAssistant', tName: 'screens.onboardOrg.modAI', tDesc: 'screens.onboardOrg.modAIDesc', icon: 'activity' },
  { key: 'bordereaux', tName: 'screens.onboardOrg.modBordereaux', tDesc: 'screens.onboardOrg.modBordereauxDesc', icon: 'bar-chart' },
  { key: 'riskSignals', tName: 'screens.onboardOrg.modRiskSignals', tDesc: 'screens.onboardOrg.modRiskSignalsDesc', icon: 'alert-triangle' },
  { key: 'messaging', tName: 'screens.onboardOrg.modMessaging', tDesc: 'screens.onboardOrg.modMessagingDesc', icon: 'message-circle' },
  { key: 'videoConference', tName: 'screens.onboardOrg.modVideo', tDesc: 'screens.onboardOrg.modVideoDesc', icon: 'video' },
];

const INDUSTRIES = ['General Insurance', 'Health', 'Property & Casualty', 'Life', 'Reinsurance'];
const COUNTRIES = ['United States', 'United Kingdom', 'Switzerland', 'Germany', 'France', 'United Arab Emirates', 'Canada', 'Australia', 'India', 'Singapore'];
const TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Europe/Zurich', 'Europe/Berlin', 'Europe/Paris', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Kolkata', 'Australia/Sydney'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'AED', 'CAD', 'AUD', 'INR', 'SGD'];

// ─── Shared Styles ───────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 14px', background: '#fff',
  border: `1px solid ${S.borderLight}`, borderRadius: 8,
  fontSize: 13, color: S.textPrimary, outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: S.textSecondary, marginBottom: 6, display: 'block' };

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

// ─── Toggle Switch ───────────────────────────────────────
const Toggle = ({ checked, onChange, disabled }) => (
  <div
    onClick={disabled ? undefined : onChange}
    style={{
      width: 44, height: 24, borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer',
      background: disabled ? '#e2e8f0' : checked ? S.burgundy : '#cbd5e1',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <div style={{
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 3, left: checked ? 23 : 3,
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </div>
);

// ─── Progress Bar ────────────────────────────────────────
const ProgressBar = ({ label, used, max, unit, color }) => {
  const { t } = useTranslation();
  const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{label}</span>
        <span style={{ fontSize: 12, color: S.textMuted }}>{used} / {max} {unit}</span>
      </div>
      <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || S.burgundy, borderRadius: 4, transition: 'width 0.3s' }} />
      </div>
      <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>{t('screens.orgDetail.pctUsed', { pct: pct.toFixed(1) })}</div>
    </div>
  );
};

// ─── Metric Card ─────────────────────────────────────────
const MetricCard = ({ label, value, icon, color }) => (
  <div style={{
    flex: 1, background: '#fff', borderRadius: 12, padding: '20px 18px',
    border: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', gap: 14,
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 10, background: color + '14',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon name={icon} color={color} size={20} />
    </div>
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: S.textPrimary, lineHeight: 1 }}>{value}</div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════
const OrgDetailScreen = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Users tab
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Modules tab
  const [editModules, setEditModules] = useState({});

  // Settings tab
  const [settingsForm, setSettingsForm] = useState({
    timezone: '', currency: '', country: '', industry: '', primaryColor: '',
  });

  const fetchOrg = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/orgs/${id}/overview`, { withCredentials: true });
      if (resp.data?.data) {
        const { org: orgData, metrics } = resp.data.data;
        // Merge org data with metrics so we can access everything from `org` state
        const merged = {
          ...orgData,
          userCount: metrics.users,
          openClaimsCount: metrics.claims.open,
          activePoliciesCount: metrics.policies,
          storageUsed: metrics.storage.usedMB,
          maxStorageGB: metrics.storage.maxGB,
        };
        setOrg(merged);
        // Init module toggles
        const mods = {};
        MODULES.forEach(m => { mods[m.key] = orgData.modules?.[m.key] || m.core; });
        setEditModules(mods);
        // Init settings form
        setSettingsForm({
          timezone: orgData.timezone || '',
          currency: orgData.currency || LOCALE_CONFIG.currency,
          country: orgData.country || '',
          industry: orgData.industry || '',
          primaryColor: orgData.primaryColor || '#800020',
        });
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const resp = await axios.get(`${BASE_URL}/users?org=${id}`, { withCredentials: true });
      if (resp.data?.data) {
        const userData = Array.isArray(resp.data.data) ? resp.data.data : resp.data.data.data || [];
        setUsers(userData);
      }
    } catch {
      // silent
    } finally {
      setUsersLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);
  useEffect(() => { if (activeTab === 'users') fetchUsers(); }, [activeTab, fetchUsers]);

  const handleToggleActive = async () => {
    try {
      await axios.patch(`${BASE_URL}/orgs/${id}/settings`, { active: !org.active }, { withCredentials: true });
      setOrg(prev => ({ ...prev, active: !prev.active }));
    } catch {
      // silent
    }
  };

  const handleSaveModules = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await axios.patch(`${BASE_URL}/orgs/${id}/modules`, { modules: editModules }, { withCredentials: true });
      setSaveMsg(t('screens.orgDetail.modulesSaved'));
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg(t('screens.orgDetail.modulesFail'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await axios.patch(`${BASE_URL}/orgs/${id}/settings`, settingsForm, { withCredentials: true });
      setSaveMsg(t('screens.orgDetail.settingsSaved'));
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg(t('screens.orgDetail.settingsFail'));
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id: 'overview', label: t('screens.orgDetail.tabOverview') },
    { id: 'users', label: t('screens.orgDetail.tabUsers') },
    { id: 'modules', label: t('screens.orgDetail.tabModules') },
    { id: 'settings', label: t('screens.orgDetail.tabSettings') },
    { id: 'usage', label: t('screens.orgDetail.tabUsage') },
  ];

  if (loading) {
    return (
      <AppLayout>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: S.burgundy, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </AppLayout>
    );
  }

  if (!org) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: S.textPrimary, marginBottom: 8 }}>{t('screens.orgDetail.notFound')}</div>
          <BtnSecondary onClick={() => history.push('/platform')}>
            <Icon name="arrowLeft" color={S.textSecondary} size={16} /> {t('screens.orgDetail.backToPlatform')}
          </BtnSecondary>
        </div>
      </AppLayout>
    );
  }

  const formatStorage = (bytes) => {
    if (!bytes) return '0';
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1);
    return (bytes / 1048576).toFixed(0);
  };

  // ─── Tab: Overview ─────────────────────────────────────
  const renderOverview = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        <MetricCard label={t('screens.orgDetail.metricTotalUsers')} value={org.userCount ?? users.length ?? 0} icon="users" color={S.info} />
        <MetricCard label={t('screens.orgDetail.metricOpenClaims')} value={org.openClaimsCount ?? 0} icon="shield" color={S.warning} />
        <MetricCard label={t('screens.orgDetail.metricActivePolicies')} value={org.activePoliciesCount ?? 0} icon="file-check" color={S.success} />
        <MetricCard label={t('screens.orgDetail.metricStorage')} value={t('screens.orgDetail.mbSuffix', { n: formatStorage(org.storageUsed || 0) })} icon="folder" color={S.burgundy} />
      </div>
      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 16 }}>{t('screens.orgDetail.recentActivity')}</div>
        <div style={{ padding: '40px 20px', textAlign: 'center', color: S.textMuted, fontSize: 13 }}>
          <Icon name="clock" color="#94a3b8" size={24} />
          <div style={{ marginTop: 8 }}>{t('screens.orgDetail.activityComingSoon')}</div>
        </div>
      </div>
    </div>
  );

  // ─── Tab: Users ────────────────────────────────────────
  const renderUsers = () => (
    <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${S.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{users.length !== 1 ? t('screens.orgDetail.usersCountPlural', { count: users.length }) : t('screens.orgDetail.usersCount', { count: users.length })}</div>
        <BtnPrimary onClick={() => history.push(`/users?org=${id}`)} style={{ padding: '8px 16px', fontSize: 12 }}>
          <Icon name="plus" color="#fff" size={14} /> {t('screens.orgDetail.addUser')}
        </BtnPrimary>
      </div>
      {usersLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ width: 24, height: 24, border: '2px solid #e2e8f0', borderTopColor: S.burgundy, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <TH>{t('screens.orgDetail.colName')}</TH>
              <TH>{t('screens.orgDetail.colEmail')}</TH>
              <TH>{t('screens.orgDetail.colRole')}</TH>
              <TH>{t('screens.orgDetail.colStatus')}</TH>
              <TH>{t('screens.orgDetail.colLastLogin')}</TH>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <TD colSpan="5" style={{ textAlign: 'center', padding: '40px 14px', color: S.textMuted }}>
                  {t('screens.orgDetail.noUsers')}
                </TD>
              </tr>
            )}
            {users.map((user, i) => (
              <tr key={user._id} style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard }}>
                <TD>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 11, fontWeight: 700,
                    }}>
                      {(user.name || 'U').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <span style={{ fontWeight: 600, color: S.textPrimary }}>{user.name || '—'}</span>
                  </div>
                </TD>
                <TD>{user.email || '—'}</TD>
                <TD>
                  <span style={{ fontSize: 12, fontWeight: 600, color: S.textSecondary, textTransform: 'capitalize' }}>
                    {(user.role || 'user').replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </TD>
                <TD>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: user.active !== false ? '#dcfce7' : '#fee2e2',
                    color: user.active !== false ? '#15803d' : '#991b1b',
                  }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: user.active !== false ? '#15803d' : '#991b1b' }} />
                    {user.active !== false ? t('screens.orgDetail.userActive') : t('screens.orgDetail.userInactive')}
                  </span>
                </TD>
                <TD>{user.lastLoginAt ? formatDate(user.lastLoginAt) : '—'}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // ─── Tab: Modules ──────────────────────────────────────
  const renderModules = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.orgDetail.moduleConfigTitle')}</div>
          <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{t('screens.orgDetail.moduleConfigBlurb')}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {saveMsg && (
            <span style={{ fontSize: 12, fontWeight: 600, color: saveMsg === t('screens.orgDetail.modulesSaved') || saveMsg === t('screens.orgDetail.settingsSaved') ? S.success : S.danger }}>{saveMsg}</span>
          )}
          <BtnPrimary onClick={handleSaveModules} disabled={saving}>
            {saving ? t('screens.orgDetail.saving') : t('screens.orgDetail.saveModules')}
          </BtnPrimary>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {MODULES.map(mod => {
          const enabled = editModules[mod.key];
          return (
            <div key={mod.key} style={{
              background: '#fff', border: `1px solid ${enabled ? S.burgundy + '40' : S.borderLight}`,
              borderRadius: 12, padding: '20px 18px',
              opacity: mod.core ? 0.85 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: enabled ? S.burgundy + '14' : '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name={mod.icon} color={enabled ? S.burgundy : S.textMuted} size={20} />
                </div>
                <Toggle
                  checked={enabled}
                  onChange={() => {
                    if (!mod.core) setEditModules(prev => ({ ...prev, [mod.key]: !prev[mod.key] }));
                  }}
                  disabled={mod.core}
                />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary, marginBottom: 4 }}>{t(mod.tName)}</div>
              <div style={{ fontSize: 12, color: S.textMuted }}>{t(mod.tDesc)}</div>
              {mod.core && (
                <div style={{ fontSize: 10, fontWeight: 700, color: S.burgundy, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 8 }}>{t('screens.orgDetail.coreModule')}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── Tab: Settings ─────────────────────────────────────
  const renderSettings = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.orgDetail.settingsTitle')}</div>
          <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{t('screens.orgDetail.settingsBlurb')}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {saveMsg && (
            <span style={{ fontSize: 12, fontWeight: 600, color: saveMsg === t('screens.orgDetail.modulesSaved') || saveMsg === t('screens.orgDetail.settingsSaved') ? S.success : S.danger }}>{saveMsg}</span>
          )}
          <BtnPrimary onClick={handleSaveSettings} disabled={saving}>
            {saving ? t('screens.orgDetail.saving') : t('screens.orgDetail.saveSettings')}
          </BtnPrimary>
        </div>
      </div>
      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, padding: 24, maxWidth: 600 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
          <Field label={t('screens.orgDetail.timezone')}>
            <select style={inputStyle} value={settingsForm.timezone} onChange={e => setSettingsForm(prev => ({ ...prev, timezone: e.target.value }))}>
              <option value="">{t('screens.orgDetail.selectTimezone')}</option>
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Field>
          <Field label={t('screens.orgDetail.currency')}>
            <select style={inputStyle} value={settingsForm.currency} onChange={e => setSettingsForm(prev => ({ ...prev, currency: e.target.value }))}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={t('screens.orgDetail.country')}>
            <select style={inputStyle} value={settingsForm.country} onChange={e => setSettingsForm(prev => ({ ...prev, country: e.target.value }))}>
              <option value="">{t('screens.orgDetail.selectCountry')}</option>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={t('screens.orgDetail.industry')}>
            <select style={inputStyle} value={settingsForm.industry} onChange={e => setSettingsForm(prev => ({ ...prev, industry: e.target.value }))}>
              <option value="">{t('screens.orgDetail.selectIndustry')}</option>
              {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t('screens.orgDetail.primaryColor')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="color"
              value={settingsForm.primaryColor}
              onChange={e => setSettingsForm(prev => ({ ...prev, primaryColor: e.target.value }))}
              style={{ width: 40, height: 36, padding: 0, border: `1px solid ${S.borderLight}`, borderRadius: 6, cursor: 'pointer' }}
            />
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={settingsForm.primaryColor}
              onChange={e => setSettingsForm(prev => ({ ...prev, primaryColor: e.target.value }))}
              placeholder="#800020"
            />
          </div>
        </Field>
      </div>
    </div>
  );

  // ─── Tab: Usage ────────────────────────────────────────
  const renderUsage = () => {
    const quotas = org.quotas || {};
    const maxUsersQ = quotas.maxUsers || 50;
    const maxStorageGBQ = quotas.maxStorageGB || 10;
    const currentUsers = org.userCount ?? users.length ?? 0;
    const storageUsedGB = (org.storageUsed || 0) / 1073741824;

    return (
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 20 }}>{t('screens.orgDetail.resourceUsage')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, padding: 24 }}>
            <ProgressBar
              label={t('screens.orgDetail.storageLabel')}
              used={storageUsedGB.toFixed(2)}
              max={maxStorageGBQ}
              unit="GB"
              color={storageUsedGB / maxStorageGBQ > 0.8 ? S.danger : S.burgundy}
            />
            <ProgressBar
              label={t('screens.orgDetail.usersLabel')}
              used={currentUsers}
              max={maxUsersQ}
              unit={t('screens.orgDetail.seats')}
              color={currentUsers / maxUsersQ > 0.8 ? S.warning : S.info}
            />
          </div>
          <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, padding: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary, marginBottom: 16 }}>{t('screens.orgDetail.apiUsage')}</div>
            <div style={{ padding: '40px 20px', textAlign: 'center', color: S.textMuted, fontSize: 13 }}>
              <Icon name="bar-chart" color="#94a3b8" size={24} />
              <div style={{ marginTop: 8 }}>{t('screens.orgDetail.apiUsageComingSoon', { limit: quotas.apiRateLimit || 1000 })}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TAB_RENDERERS = { overview: renderOverview, users: renderUsers, modules: renderModules, settings: renderSettings, usage: renderUsage };

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>

        {/* Header */}
        <header style={{
          padding: '20px 32px', borderBottom: `1px solid ${S.borderLight}`,
          background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                onClick={() => history.push('/platform')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
              >
                <Icon name="arrowLeft" color={S.textMuted} size={20} />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                  background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 16, fontWeight: 700,
                }}>
                  {(org.name || 'O').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary }}>{org.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                    {org.email && <span style={{ fontSize: 12, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="mail" color={S.textMuted} size={12} />{org.email}</span>}
                    {org.phone && <span style={{ fontSize: 12, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="phone" color={S.textMuted} size={12} />{org.phone}</span>}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {org.industry && (
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: '#dbeafe', color: '#1d4ed8' }}>
                  {org.industry}
                </span>
              )}
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: org.active !== false ? '#dcfce7' : '#fee2e2',
                color: org.active !== false ? '#15803d' : '#991b1b',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: org.active !== false ? '#15803d' : '#991b1b' }} />
                {org.active !== false ? t('screens.orgDetail.orgActive') : t('screens.orgDetail.orgInactive')}
              </span>
              <Toggle checked={org.active !== false} onChange={handleToggleActive} />
            </div>
          </div>
        </header>

        {/* Tab Bar */}
        <div style={{
          display: 'flex', gap: 0, borderBottom: `1px solid ${S.borderLight}`,
          padding: '0 32px', background: 'rgba(255,255,255,0.5)', flexShrink: 0,
        }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSaveMsg(''); }}
              style={{
                padding: '14px 20px', fontSize: 13, fontWeight: 600,
                color: activeTab === tab.id ? S.burgundy : S.textMuted,
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: activeTab === tab.id ? `2px solid ${S.burgundy}` : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, padding: '24px 32px', minHeight: 0 }}>
          {TAB_RENDERERS[activeTab]?.()}
        </div>
      </div>
    </AppLayout>
  );
};

export default OrgDetailScreen;
